import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DataTable,
  Field,
  HStack,
  Heading,
  Primitive,
  TextField,
  VStack,
} from '@rtkwlf/fenrir-react';
import { faInfoCircle } from '@rtkwlf/pro-solid-svg-icons/faInfoCircle';
import { faSearch } from '@rtkwlf/pro-solid-svg-icons/faSearch';
import {
  SpidrState,
  type SecurityFocusFull,
  type Spidr,
} from '@rtkwlf/track-public';
import { useQuery } from '@tanstack/react-query';
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import type { TFunction } from 'i18next';

import { useGetOrganizationOptions } from '@apis/track-public/query-options';
import { EmptyResults } from '@packages/components/empty-results/empty-results';

import { ConciergeTierBadge } from '../../../../components/concierge-tier-badge/concierge-tier-badge';
import { LinkButton } from '../../../../components/link-button';
import type { SpidrStateBadgeProps } from '../../../../components/spidr-state-badge';
import { SpidrStateBadge } from '../../../../components/spidr-state-badge';
import { TableCellList } from '../../../../components/table-cell-list';
import { routes } from '../../../../constants/routes';
import { useSecurityJourneyTranslations } from '../../../../locales/use-security-journey-translations';
import { sortExpirationRows } from '../../../../utils/expiry-date';
import { getDaysAgo } from '../../../../utils/get-days-ago';
import { getDaysToExpiry } from '../../../../utils/get-days-to-expiry';

import styles from './security-focus-spidrs-table.module.css';

/* -------------------------------------------------------------------------------------------------
 * ExpirationColumn
 * -----------------------------------------------------------------------------------------------*/
const ExpirationColumn: React.FC<{
  spidr: Spidr;
}> = ({ spidr }) => {
  const { t } = useSecurityJourneyTranslations();

  const deliveredOrCompleteStates: SpidrState[] = [
    SpidrState.Delivered,
    SpidrState.Complete,
  ];

  const isDeliveredOrComplete = deliveredOrCompleteStates.includes(spidr.state);

  const outcomeExpiration = React.useMemo(() => {
    if (!isDeliveredOrComplete && spidr.blueprint.doesExpire) {
      return t('focusID.securityFocusSpidrsTable.expirationColumn.tbd');
    } else if (
      isDeliveredOrComplete &&
      spidr.blueprint.doesExpire &&
      spidr.expiryDate
    ) {
      return getDaysToExpiry(spidr.expiryDate);
    } else {
      return t('focusID.securityFocusSpidrsTable.expirationColumn.noExpiry');
    }
  }, [isDeliveredOrComplete, spidr.blueprint.doesExpire, spidr.expiryDate, t]);

  return <div>{outcomeExpiration}</div>;
};

/* -------------------------------------------------------------------------------------------------
 * ActionColumn
 * -----------------------------------------------------------------------------------------------*/
const ActionColumn: React.FC<{
  spidr: Spidr;
  focusID: string;
}> = ({ spidr, focusID }) => {
  const { t } = useSecurityJourneyTranslations();
  const isSpidrViewable =
    spidr.state === SpidrState.Delivered ||
    spidr.state === SpidrState.Complete ||
    spidr.state === SpidrState.Legacy;

  if (!isSpidrViewable) {
    return null;
  }
  return (
    <HStack xAlign="right">
      <LinkButton
        data-testid={`view-spidr-button-${spidr.id}`}
        href={routes.securityFocuses.focusID.spidrs.spidrID.index({
          spidrID: spidr.id,
          focusID,
        })}
        variant="tertiary"
      >
        {t('focusID.securityFocusSpidrsTable.actions.viewSpidr')}
      </LinkButton>
    </HStack>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Columns
 * -----------------------------------------------------------------------------------------------*/
const getColumns = (
  t: TFunction<'translation', undefined>,
  focusID: string,
): ColumnDef<Spidr>[] => [
  {
    id: 'name',
    accessorKey: 'blueprint.name',
    header: t('focusID.securityFocusSpidrsTable.headers.name'),
    cell: ({ row }) => (
      <div className={styles['nameColumn']}>{row.original.blueprint.name}</div>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'state',
    id: 'state',
    header: t('focusID.securityFocusSpidrsTable.headers.status'),
    enableSorting: true,
    cell: ({ row }) => (
      <SpidrStateBadge
        state={row.original.state as SpidrStateBadgeProps['state']}
      />
    ),
    meta: {
      width: 150,
    },
  },
  {
    id: 'tier',
    header: t('focusID.securityFocusSpidrsTable.headers.tier'),
    cell: ({ row }) => (
      <ConciergeTierBadge.Spidr tier={row.original.blueprint.tier} />
    ),
  },
  {
    id: 'technicalDomains',
    header: t('focusID.securityFocusSpidrsTable.headers.technicalDomains'),
    cell: ({ row }) => (
      <TableCellList items={row.original.blueprint.technicalDomains} />
    ),
  },
  {
    id: 'technologies',
    header: t('focusID.securityFocusSpidrsTable.headers.technologies'),
    cell: ({ row }) => (
      <TableCellList items={row.original.blueprint.technologies} />
    ),
  },
  {
    id: 'nist',
    header: t('focusID.securityFocusSpidrsTable.headers.nist'),
    cell: ({ row }) => <TableCellList items={row.original.blueprint.nist} />,
  },
  // TODO: add cis column back once we update table to use fenrir V2 (see S2CX-8967)
  // {
  //   id: 'cis',
  //   header: t('focusID.securityFocusSpidrsTable.headers.cis'),
  //   cell: ({ row }) => <TableCellList items={row.original.blueprint.cis} />,
  // },
  {
    accessorKey: 'deliveredAt',
    header: t('focusID.securityFocusSpidrsTable.headers.deliveredAt'),
    enableSorting: true,
    cell: ({ row }) =>
      row.original.deliveredAt &&
      getDaysAgo(row.original.deliveredAt, t('common.spidrStates.delivered')),
  },
  {
    accessorKey: 'expiryDate',
    id: 'outcomeExpiration',
    header: t('focusID.securityFocusSpidrsTable.headers.outcomeExpiration'),
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      return sortExpirationRows(rowA.original, rowB.original);
    },
    cell: ({ row }) => <ExpirationColumn spidr={row.original} />,
  },
  {
    id: '_actions',
    header: '',
    cell: ({ row }) => <ActionColumn spidr={row.original} focusID={focusID} />,
    meta: {
      align: 'right',
    },
  },
];

/* -------------------------------------------------------------------------------------------------
 * Security Focus SPiDRs Table
 * -----------------------------------------------------------------------------------------------*/
export type SecurityFocusSpidrsTableProps = {
  focus: SecurityFocusFull;
};

export const SecurityFocusSpidrsTable: React.FC<
  SecurityFocusSpidrsTableProps
> = ({ focus }) => {
  const [search, setSearch] = React.useState('');

  const { t } = useSecurityJourneyTranslations();

  const { data: organization } = useQuery(useGetOrganizationOptions());

  const isOrganizationTierNonLegacy = React.useMemo(() => {
    return organization?.tierInfo && organization.tierInfo.legacy === false;
  }, [organization]);

  const rows = React.useMemo((): Spidr[] => {
    if (focus.spidrs.length === 0) return [];

    return focus.spidrs.filter((spidr: Spidr) => {
      if (search !== '') {
        const s = search.toLowerCase();

        if (!spidr.blueprint.name.toLowerCase().includes(s)) {
          return false;
        }
      }

      return true;
    });
  }, [focus.spidrs, search]);

  const columns = React.useMemo(() => {
    let tempColumns = getColumns(t, focus.id);
    if (isOrganizationTierNonLegacy) {
      return tempColumns;
    }
    tempColumns = tempColumns.filter((c) => c.id !== 'tier');
    return tempColumns;
  }, [focus.id, isOrganizationTierNonLegacy, t]);

  const table = useReactTable({
    columns,
    data: rows,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    sortDescFirst: false,
  });

  return (
    <VStack width="full" data-testid="security-focus-spidrs-table">
      <Heading
        level="2"
        styledAs="heading3"
        marginBottom="medium"
        marginTop="default"
      >
        {t('focusID.securityFocusSpidrsTable.title')}
      </Heading>
      <Primitive.div width="18rem">
        <Field.Root>
          <Field.Label htmlFor="search">{t('common.search')}</Field.Label>
          <Field.Content>
            <TextField
              id="search"
              name="search"
              type="text"
              fontSize="default"
              fontWeight="normal"
              placeholder={t(
                'focusID.securityFocusSpidrsTable.searchPlaceholder',
              )}
              iconRight={<FontAwesomeIcon icon={faSearch} />}
              width="auto"
              value={search}
              onChange={(e) => {
                setSearch(e.currentTarget.value);
              }}
            />
          </Field.Content>
          <Field.Message />
        </Field.Root>
      </Primitive.div>
      {focus.spidrs.length === 0 ? (
        <EmptyResults
          variant="banner"
          icon={faInfoCircle}
          title={t('focusID.securityFocusSpidrsTable.noResultMessage.title')}
          description={t(
            'focusID.securityFocusSpidrsTable.noResultMessage.description',
          )}
        />
      ) : (
        <DataTable table={table} stickyLastColumn />
      )}
    </VStack>
  );
};
